import { useRef, useCallback } from 'react'
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api'
import { Input } from '@/components/ui/input'
import { Loader2, MapPin } from 'lucide-react'
import { GOOGLE_MAPS_LOADER_OPTIONS } from '@/lib/maps/loader'

export interface HubAddressValue {
  address: string
  lat: number
  lng: number
}

interface HubAddressInputProps {
  value: HubAddressValue
  onChange: (value: HubAddressValue) => void
  placeholder?: string
  disabled?: boolean
}

export function HubAddressInput({
  value,
  onChange,
  placeholder = 'Enter your business address',
  disabled = false,
}: HubAddressInputProps) {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)

  // Load Google Maps API. Options MUST match every other Builder
  // useJsApiLoader call site (see src/lib/maps/loader.ts) or the loader throws
  // "Loader must not be called again with different options".
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS)

  // Handle autocomplete load
  const onAutocompleteLoad = useCallback(
    (autocomplete: google.maps.places.Autocomplete) => {
      autocompleteRef.current = autocomplete
    },
    []
  )

  // Handle place selection
  const onPlaceChanged = useCallback(() => {
    const autocomplete = autocompleteRef.current
    if (!autocomplete) return

    const place = autocomplete.getPlace()
    if (!place.geometry?.location) {
      console.warn('No location data for selected place')
      return
    }

    const lat = place.geometry.location.lat()
    const lng = place.geometry.location.lng()
    const address = place.formatted_address || ''

    onChange({ address, lat, lng })
  }, [onChange])

  // Handle manual input change (clear coordinates when manually editing)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      address: e.target.value,
      lat: 0,
      lng: 0,
    })
  }

  if (loadError) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Failed to load Google Maps. Please check your API key.</span>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-muted-foreground">Loading address input...</span>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Autocomplete
        onLoad={onAutocompleteLoad}
        onPlaceChanged={onPlaceChanged}
        options={{
          types: ['address'],
          componentRestrictions: { country: 'us' },
        }}
      >
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={placeholder}
            value={value.address}
            onChange={handleInputChange}
            disabled={disabled}
            className="pl-9"
          />
        </div>
      </Autocomplete>
      {value.lat !== 0 && value.lng !== 0 && (
        <p className="text-xs text-muted-foreground">
          Coordinates: {value.lat.toFixed(6)}, {value.lng.toFixed(6)}
        </p>
      )}
    </div>
  )
}
